import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Send,
  Zap,
  LayoutGrid,
  CalendarDays,
  Users,
  MapPin,
  ChevronRight,
  MessageCircle,
  PartyPopper,
  UsersRound,
  X,
  LogIn,
  Calendar,
  ChevronDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import QuizModal from "@/components/QuizModal";
import ThalesFooter from "@/components/thales/ThalesFooter";
import AIPlannerFloating from "@/components/thales/AIPlannerFloating";
import ThalesEventTypes from "@/components/thales/ThalesEventTypes";
import ThalesServices from "@/components/thales/ThalesServices";
import ThalesPreferredTerms from "@/components/thales/ThalesPreferredTerms";
import ThalesHowTo from "@/components/thales/ThalesHowTo";
import OmbelineContactPopover from "@/components/thales/OmbelineContactPopover";
import { withUtm } from "@/components/thales/utm";
import { useArkemaLang } from "@/components/thales/i18n";
import natachaImg from "@/assets/colleagues/natacha.png";
import camilleImg from "@/assets/colleagues/camille.png";
import charlineImg from "@/assets/colleagues/charline.png";
import julieImg from "@/assets/colleagues/julie.png";

const NABOO_LOGO = "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/logo.naboo.email.png";
const BPI_LOGO = "/lovable-uploads/ayvens-societe-generale.png";
const OMBELINE_PHOTO = "/lovable-uploads/ombeline.jpg";
const HERO_BG = "/lovable-uploads/villa-st-tropez-quiz-bg.webp";

const QUIZ_URL = "https://fr.ext.naboo.app/destinations";
const CLIENT_URL = "https://sg.naboo.app/";

const venues = [
  {
    name: "Kasteel van Ordingen",
    location: "Belgique",
    specs: "",
    image: "/lovable-uploads/5af6d75b-bc82-4752-9ead-ca776ea7c089.png",
    url: "https://www.naboo.app/explorer/houses/kasteel-van-ordingen",
  },
  {
    name: "El Rincón",
    location: "Espagne",
    specs: "",
    image: "/lovable-uploads/84e950ac-1e48-42aa-b2bc-e416dd561550.png",
    url: "https://www.naboo.app/explorer/houses/el-rincon",
  },
  {
    name: "Domaine de Maffliers",
    location: "France",
    specs: "",
    image: "/lovable-uploads/a9e5bd65-c5b7-4751-8a18-f5e362c61240.png",
    url: "https://www.naboo.app/explorer/houses/63ea5494a135ab9ff7583bf8",
  },
  {
    name: "Port Lympne Hotel",
    location: "Royaume-Uni",
    specs: "",
    image: "/lovable-uploads/ade02424-d588-4166-a844-571b21592735.png",
    url: "https://www.naboo.app/explorer/houses/67067852dfe9d4d3c7d6f005",
  },
];

const ext = { target: "_blank", rel: "noopener noreferrer" } as const;

const Thales = () => {
  const { lang, t, switchPath } = useArkemaLang();
  const [showFloating, setShowFloating] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [pushClosed, setPushClosed] = useState(false);

  const stats = [
    { value: t("stats.partnership_value"), label: t("stats.partnership"), green: false, icon: Calendar },
    { value: "420", label: t("stats.events"), green: true, icon: PartyPopper },
    { value: "41 585", label: t("stats.participants"), green: false, icon: UsersRound },
    { value: "191", label: t("stats.colleagues"), green: true, icon: Users },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFloating(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className="min-h-screen bg-white text-[#111111] overflow-x-hidden pt-16 sm:pt-20"
      style={{ scrollBehavior: "smooth" }}
    >
      <Helmet>
        <html lang={t("html.lang")} />
        <title>{t("seo.title")}</title>
        <meta name="description" content={t("seo.description")} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://sg.naboo.app/" />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Naboo × Ayvens - Société Générale" />
        <meta property="og:title" content={t("seo.title")} />
        <meta property="og:description" content={t("seo.og.description")} />
        <meta property="og:image" content="/lovable-uploads/ayvens-societe-generale.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={t("seo.og.image_alt")} />
        <meta property="og:url" content="https://sg.naboo.app/" />
        <meta property="og:locale" content={t("seo.locale")} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("seo.twitter.title")} />
        <meta name="twitter:description" content={t("seo.twitter.description")} />
        <meta name="twitter:image" content="/lovable-uploads/ayvens-societe-generale.png" />
        <meta name="twitter:image:alt" content={t("seo.twitter.image_alt")} />

        <meta name="author" content={t("seo.author")} />
        <meta name="theme-color" content="#C6E278" />
      </Helmet>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-2 sm:gap-3 lg:gap-4">
          {/* Left: logos — same visual height */}
          <div className="flex items-center gap-1.5 sm:gap-3 lg:gap-4 flex-shrink-0 min-w-0">
            <img
              src={BPI_LOGO}
              alt="Ayvens - Société Générale"
              className="h-10 sm:h-14 w-auto object-contain block translate-y-1 sm:translate-y-2"
            />
            <div className="h-6 sm:h-8 w-px bg-black/15" />
            <a href={withUtm("https://www.naboo.app/")} {...ext} className="inline-flex items-center">
              <img src={NABOO_LOGO} alt="Naboo" className="h-6 sm:h-8 w-auto object-contain block" />
            </a>
          </div>

          {/* Center: Ombeline pill (desktop only) */}
          <OmbelineContactPopover align="center">
            <button
              type="button"
              className="hidden lg:flex items-center gap-3 bg-white border border-black/10 rounded-full pl-1.5 pr-2 py-1.5 shadow-sm hover:shadow-md transition-shadow group"
            >
              <img src={OMBELINE_PHOTO} alt="Ombeline" className="w-9 h-9 rounded-full object-cover" />
              <span className="flex flex-col leading-tight text-sm text-[#111111] text-left">
                <span>
                  <span className="font-bold">Ombeline</span>
                  <span className="text-[#6b7280]">{t("header.pill_role_line1")}</span>
                </span>
                <span className="font-bold">Ayvens - Société Générale</span>
              </span>
              <span className="inline-flex items-center gap-1.5 bg-[#111111] text-white text-xs font-semibold px-3 py-1.5 rounded-full ml-1 group-hover:opacity-90 transition-opacity">
                <MessageCircle className="w-3.5 h-3.5" />
                {t("header.discuter")}
              </span>
            </button>
          </OmbelineContactPopover>

          {/* Right: 3 CTAs (compact on tablet/mobile, full on desktop) */}
          <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 flex-shrink-0">
            <OmbelineContactPopover align="end">
              <button
                type="button"
                aria-label={t("header.discuter_aria")}
                className="lg:hidden inline-flex items-center gap-1 sm:gap-1.5 bg-white text-[#111111] text-xs font-semibold px-1.5 py-1.5 sm:px-3 sm:py-2.5 rounded-full border border-black/15 hover:border-black/40 hover:bg-black/5 transition-colors"
              >
                <img src={OMBELINE_PHOTO} alt="" className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover" />
                <span className="hidden sm:inline">{t("header.discuter")}</span>
              </button>
            </OmbelineContactPopover>

            <button
              type="button"
              onClick={() => setQuizOpen(true)}
              className="inline-flex items-center gap-1 sm:gap-2 bg-[#C6E278] text-[#111111] text-xs sm:text-sm font-semibold px-2.5 py-1.5 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 rounded-full hover:opacity-90 transition-opacity"
            >
              <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-[#C6E278]" />
              <span className="hidden xs:inline sm:hidden">{t("header.demande_short")}</span>
              <span className="hidden sm:inline">{t("header.envoyer_demande")}</span>
            </button>

            <a
              href={withUtm(CLIENT_URL)}
              {...ext}
              aria-label={t("header.plateforme_full")}
              className="inline-flex items-center gap-1.5 sm:gap-2 bg-white text-[#111111] text-xs sm:text-sm font-semibold px-1.5 py-1.5 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 rounded-full border border-black/15 hover:border-black/40 hover:bg-black/5 transition-colors"
            >
              <LogIn className="w-3.5 h-3.5 sm:hidden" />
              <span className="hidden sm:inline lg:hidden">{t("header.plateforme_short")}</span>
              <span className="hidden lg:flex flex-col leading-tight text-left">
                <span>{t("header.plateforme_l1")}</span>
                <span>{t("header.plateforme_l2")}</span>
              </span>
            </a>

            {/* Lang switcher (dropdown with flag) — placed at far right */}
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs font-semibold text-[#111111] hover:bg-black/5 transition-colors focus:outline-none">
                <span className="text-base leading-none">{({ en: "🇬🇧", fr: "🇫🇷" } as const)[lang]}</span>
                <span className="uppercase">{lang}</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[10rem] bg-white z-[100000]">
                {[
                  { code: "en" as const, label: "English", flag: "🇬🇧" },
                  { code: "fr" as const, label: "Français", flag: "🇫🇷" },
                ].map((l) => (
                  <DropdownMenuItem key={l.code} asChild>
                    <Link to={switchPath(l.code)} className="flex items-center gap-2 cursor-pointer">
                      <span className="text-base leading-none">{l.flag}</span>
                      <span className="uppercase text-xs font-semibold">{l.code}</span>
                      <span>— {l.label}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* HERO — photo lieu premium en background, gros titre blanc centré, CTA vert */}
      <section
        className="relative bg-[#0a0f1e]"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Dark overlay pour lisibilité (style espace client) */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/65" />

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 lg:py-14">
          <div className="max-w-4xl mx-auto text-center text-white">
            <span className="inline-flex items-center gap-2 bg-[#C6E278] text-[#111111] text-xs font-bold uppercase tracking-wider px-5 py-2 rounded-full mb-5">
              <Zap className="w-4 h-4 fill-[#111111]" />
              {t("hero.badge")}
            </span>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight tracking-tight mb-6 max-w-3xl mx-auto">
              {t("hero.title")}
            </h1>

            {/* Steps row with icons */}
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3 mb-6 text-white">
              <span className="inline-flex items-center gap-2 text-sm sm:text-base">
                <LayoutGrid className="w-5 h-5 text-[#C6E278]" />
                {t("hero.step.type")}
              </span>
              <ChevronRight className="w-4 h-4 text-[#C6E278]" />
              <span className="inline-flex items-center gap-2 text-sm sm:text-base">
                <CalendarDays className="w-5 h-5 text-[#C6E278]" />
                {t("hero.step.dates")}
              </span>
              <ChevronRight className="w-4 h-4 text-[#C6E278]" />
              <span className="inline-flex items-center gap-2 text-sm sm:text-base">
                <Users className="w-5 h-5 text-[#C6E278]" />
                {t("hero.step.participants")}
              </span>
              <ChevronRight className="w-4 h-4 text-[#C6E278]" />
              <span className="inline-flex items-center gap-2 text-sm sm:text-base">
                <MapPin className="w-5 h-5 text-[#C6E278]" />
                {t("hero.step.match")}
              </span>
            </div>

            <button
              type="button"
              onClick={() => setQuizOpen(true)}
              className="inline-flex items-center gap-2 bg-[#C6E278] text-[#111111] text-sm font-semibold px-6 py-3 rounded-full hover:opacity-90 hover:scale-105 transition-all"
            >
              {t("hero.cta")}
            </button>

            <p className="text-sm text-white/80 mt-4">{t("hero.subline")}</p>
          </div>
        </div>
      </section>

      {/* STATS BAR — chiffres clés en bandeau juste après le hero */}
      <section className="bg-white border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-6 gap-x-4 divide-y sm:divide-y-0 sm:divide-x divide-black/10">
            {stats.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="px-4 lg:px-6 text-center first:pt-0 [&:nth-child(2)]:pt-0 pt-6 lg:pt-0">
                  <div className="flex items-center justify-center gap-2">
                    {Icon && (
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: s.green ? "#a8c45e" : "#111111" }} />
                    )}
                    <div
                      className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight"
                      style={{ color: s.green ? "#a8c45e" : "#111111" }}
                    >
                      {s.value}
                    </div>
                  </div>
                  <div className="text-xs sm:text-sm text-[#6b7280] mt-1">{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-4 sm:pt-6 sm:pb-6">
          <div className="bg-gradient-to-br from-[#fafbf5] to-[#f0f3e8] rounded-3xl px-8 sm:px-12 lg:px-16 pt-5 pb-5 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8 border border-black/5">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight mb-4 text-[#111111]">
              {t("colleagues.title")}
            </h2>
            <p className="text-sm sm:text-base text-[#6b7280] mb-7 max-w-xl">{t("colleagues.paragraph")}</p>
            <a
              href={withUtm(CLIENT_URL)}
              {...ext}
              className="inline-flex items-center gap-2 bg-[#111111] text-white text-sm font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity"
            >
              {t("colleagues.cta")}
            </a>
          </div>
        </div>
      </section>

      {/* EVENT TYPES — Organisez tout type d'événement */}
      <ThalesEventTypes />

      {/* SERVICES — Réservez tout type de services */}
      <ThalesServices />

      {/* PREFERRED TERMS — Bénéficiez de conditions préférentielles */}
      <ThalesPreferredTerms />

      {/* HOW TO — Comment faire ? */}
      <ThalesHowTo />

      {/* TOP DESTINATIONS THALES */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-6 sm:pt-6 sm:pb-8">
          <div className="mb-4 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#111111]">{t("destinations.title")}</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {venues.map((v) => (
              <a
                key={v.name}
                href={withUtm(v.url)}
                {...ext}
                className="group bg-white rounded-2xl border border-black/5 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={v.image}
                    alt={v.name}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur text-[#111111] text-xs font-medium px-3 py-1.5 rounded-full shadow-sm">
                    <Zap className="w-3.5 h-3.5 fill-[#C6E278] text-[#C6E278]" />
                    {t("destinations.instant")}
                  </span>
                </div>
                <div className="p-4">
                  <div className="font-semibold text-[#111111] mb-1 line-clamp-1">{v.name}</div>
                  <div className="text-sm text-[#6b7280] mb-2 line-clamp-1">
                    {(() => {
                      const m: Record<string, Record<string, string>> = {
                        en: {
                          Belgique: "Belgium",
                          Espagne: "Spain",
                          France: "France",
                          "Royaume-Uni": "United Kingdom",
                        },
                      };
                      return m[lang]?.[v.location] || v.location;
                    })()}
                  </div>
                  <div className="text-xs text-[#6b7280] line-clamp-1">{v.specs}</div>
                </div>
              </a>
            ))}
          </div>

          {/* CTA: explorer la base complète */}
          <a
            href={withUtm(CLIENT_URL)}
            {...ext}
            className="group mt-5 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gradient-to-br from-[#fafbf5] to-[#f0f3e8] rounded-2xl border border-black/5 px-6 sm:px-8 py-4 sm:py-5 hover:shadow-md transition-shadow"
          >
            <div className="text-center sm:text-left">
              <div className="text-lg sm:text-xl font-bold text-[#111111] leading-tight">
                {t("destinations.bottom_title")}
              </div>
              <div className="text-sm text-[#6b7280] mt-1">{t("destinations.bottom_subtitle")}</div>
            </div>
            <span className="inline-flex items-center gap-2 bg-[#111111] text-white text-sm font-semibold px-6 py-3 rounded-full group-hover:opacity-90 transition-opacity flex-shrink-0">
              {t("destinations.bottom_cta")}
            </span>
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <ThalesFooter />

      {/* SLIDE-IN PUSH CTA (left) — style naboo-fr */}
      {showFloating && !pushClosed && (
        <div
          onClick={() => setQuizOpen(true)}
          className="fixed z-40 left-0 top-[68px] md:top-[25%] cursor-pointer hover:opacity-90 transition-all duration-500 ease-in-out"
        >
          <div className="relative flex items-center gap-1.5 md:gap-2 shadow-lg px-2 py-1.5 pr-6 md:px-3 md:py-2 md:pr-7 rounded-r-xl md:rounded-r-2xl bg-[#c6e278]">
            <Zap className="w-3 h-3 md:w-4 md:h-4 text-gray-800 flex-shrink-0 fill-current animate-bounce" />
            <p className="text-gray-800 text-[10px] md:text-xs leading-tight font-bold">
              {t("push.line1")}
              <br />
              <span className="font-bold">{t("push.line2")}</span>
            </p>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setPushClosed(true);
              }}
              className="absolute right-0.5 md:right-1 top-1/2 -translate-y-1/2 p-0.5 md:p-1 hover:bg-black/10 rounded-full transition-colors"
              aria-label={t("push.close")}
            >
              <X className="w-3 h-3 md:w-3.5 md:h-3.5 text-gray-800" />
            </button>
          </div>
        </div>
      )}

      <QuizModal isOpen={quizOpen} onClose={() => setQuizOpen(false)} />
      <AIPlannerFloating />
    </div>
  );
};

export default Thales;
